import type { AxiosError } from "axios";
import type { UserInfo } from "@/types";
import {
  axiosInstance,
  buildAlreadyLoggedInCacheKey,
  chatbotUrl,
} from "@/utils";
import axios from "axios";
import {
  readAlreadyLoggedInCache,
  resolveCacheBreakerMs,
  writeAlreadyLoggedInCache,
  writeAlreadyLoggedInUnauthorizedCache,
} from "@/utils";

const pendingAlreadyLoggedInRequests = new Map<
  string,
  Promise<UserInfo | null>
>();

export const fetchAlreadyLoggedIn = async (
  authHost: string,
  clientId: string,
  options?: {
    chatbot?: boolean;
    cacheBreakerMin?: number;
  },
): Promise<UserInfo | null> => {
  const path = `/realms/applications/already-loggedin-resource/already-logged-in`;

  const params = new URLSearchParams({
    client_id: clientId,
  });

  params.set("fetchAvatar", "true");

  const chatbot = Boolean(options?.chatbot);
  const cacheBreakerMin = options?.cacheBreakerMin;

  if (chatbot) {
    params.set("chatbot", "true");
  }

  const ttlMs = resolveCacheBreakerMs(cacheBreakerMin);

  if (ttlMs > 0) {
    const cached = readAlreadyLoggedInCache(authHost, clientId, chatbot);
    if (cached.kind === "user") return cached.user;
    if (cached.kind === "unauthorized") return null;
  }

  const pendingKey = buildAlreadyLoggedInCacheKey(authHost, clientId, chatbot);

  const existingPending = pendingAlreadyLoggedInRequests.get(pendingKey);
  if (existingPending) {
    return existingPending;
  }

  const url = new URL(`${path}?${params.toString()}`, authHost).toString();

  const requestPromise = (async (): Promise<UserInfo | null> => {
    try {
      const res = await axiosInstance.get<UserInfo>(url);

      const data = res.data;

      const user: UserInfo = {
        firstName: data.firstName,
        lastName: data.lastName,
        avatarUrl: data.avatarUrl,
        username: data.username,
        guid: data.guid,
        installationId: data.installationId,
        ...(chatbot && data.ticket ? { ticket: data.ticket } : {}),
      };

      writeAlreadyLoggedInCache(authHost, clientId, chatbot, user, ttlMs);

      return user;
    } catch (err) {
      const axErr = err as AxiosError;

      if (axErr.response?.status === 401) {
        if (ttlMs > 0) {
          writeAlreadyLoggedInUnauthorizedCache(
            authHost,
            clientId,
            chatbot,
            ttlMs,
          );
        }
        return null;
      }

      console.error("[telekomId] already-logged-in failed:", axErr);
      return null;
    } finally {
      pendingAlreadyLoggedInRequests.delete(pendingKey);
    }
  })();

  pendingAlreadyLoggedInRequests.set(pendingKey, requestPromise);

  return requestPromise;
};

export const postChatbotChat = async (
  payload: Record<string, unknown>,
): Promise<string> => {
  const res = await axios.post<string>(chatbotUrl, payload, {
    headers: {
      "Content-Type": "application/json",
      Accept: "text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8",
    },
    responseType: "text",
    withCredentials: false,
  });

  return typeof res.data === "string" ? res.data : String(res.data);
};
